import React, { useState } from 'react';
import { Paper, Typography, Box, CircularProgress, Tabs, Tab } from '@mui/material';
import { useAppStore } from '../state/store';
import SummaryEditModal from './SummaryEditModal';

const Summary: React.FC = () => {
  const { 
    meetingTitle, 
    tldr, 
    executiveSummary, 
    detailedSummary, 
    isLoading,
    updateTldr,
    updateExecutiveSummary,
    updateDetailedSummary
  } = useAppStore();

  const [activeTab, setActiveTab] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<'tldr' | 'executive' | 'detailed' | null>(null);

  // Handler for double-click to open modal
  const handleDoubleClick = () => {
    // Open edit modal based on active tab
    if (activeTab === 0) {
      setEditingField('tldr');
    } else if (activeTab === 1) {
      setEditingField('executive');
    } else if (activeTab === 2) {
      setEditingField('detailed');
    }
    
    setEditModalOpen(true);
  };

  // Handler for saving
  const handleSave = (newValue: string) => {
    if (editingField === 'tldr') {
      updateTldr(newValue);
    } else if (editingField === 'executive') {
      updateExecutiveSummary(newValue);
    } else if (editingField === 'detailed') {
      updateDetailedSummary(newValue);
    }
    setEditModalOpen(false);
  };

  const handleCloseModal = () => {
    setEditModalOpen(false);
  };

  // Get current content based on active tab
  const getCurrentContent = () => {
    if (activeTab === 0) return tldr || 'No TLDR available';
    if (activeTab === 1) return executiveSummary || 'No Executive Summary available';
    if (activeTab === 2) return detailedSummary || 'No Detailed Summary available';
    return '';
  };

  // Get modal title
  const getModalTitle = () => {
    if (editingField === 'tldr') return 'Edit TLDR';
    if (editingField === 'executive') return 'Edit Executive Summary';
    if (editingField === 'detailed') return 'Edit Detailed Summary';
    return 'Edit';
  };

  // Get current value for modal
  const getModalValue = () => {
    if (editingField === 'tldr') return tldr;
    if (editingField === 'executive') return executiveSummary;
    if (editingField === 'detailed') return detailedSummary;
    return '';
  };

  // Get max length for modal
  const getMaxLength = () => {
    if (editingField === 'tldr') return 600;
    if (editingField === 'executive') return 1500;
    return undefined; // No limit for detailed summary
  };

  // Get placeholder for modal
  const getPlaceholder = () => {
    if (editingField === 'tldr') return 'No TLDR available';
    if (editingField === 'executive') return 'No Executive Summary available';
    if (editingField === 'detailed') return 'No Detailed Summary available';
    return '';
  };

  return (
    <>
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        {/* Title Section */}
      {meetingTitle && (
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
          {meetingTitle}
        </Typography>
      )}

        {/* Tabs */}
        <Tabs 
          value={activeTab} 
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab 
            label="TLDR" 
            sx={{ textTransform: 'none', fontWeight: 600 }}
          />
          <Tab 
            label="Executive Summary" 
            sx={{ textTransform: 'none', fontWeight: 600 }}
          />
          <Tab 
            label="Detailed Summary" 
            sx={{ textTransform: 'none', fontWeight: 600 }}
          />
        </Tabs>

        {/* Tab Content (Read-only display) */}
      <Box sx={{ 
          height: 'calc(1.5em * 5)', // Fixed height for exactly 5 lines (lineHeight 1.5 * 5 lines)
          overflowY: 'auto',
          overflowX: 'hidden',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(0,0,0,0.1)',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.3)',
          borderRadius: '3px',
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.5)',
          }
        }
      }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 80 }}>
            <CircularProgress size={24} />
          </Box>
          ) : (
            <Typography 
              variant="body1" 
              sx={{ 
                lineHeight: 1.5, 
                whiteSpace: 'pre-line',
                color: getCurrentContent().startsWith('No ') ? 'text.secondary' : 'text.primary',
                fontStyle: getCurrentContent().startsWith('No ') ? 'italic' : 'normal',
                cursor: 'text',
                userSelect: 'text',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                }
              }}
              onDoubleClick={handleDoubleClick}
            >
              {getCurrentContent()}
          </Typography>
        )}
      </Box>
    </Paper>

      {/* Edit Modal */}
      <SummaryEditModal
        open={editModalOpen}
        onClose={handleCloseModal}
        title={getModalTitle()}
        value={getModalValue()}
        onSave={handleSave}
        maxLength={getMaxLength()}
        placeholder={getPlaceholder()}
      />
    </>
  );
};

export default Summary;
